//
// Functions that consume iterators.
//

import { checkGreater } from './core.ts';

// Like Array.map for iterators.
export function map<T, U>(iter: IterableIterator<T>, callbackfn: (value: T, index: number) => U) {
  return Array.from(iter, callbackfn);
}

// Like Array.forEach for iterators.
export function forEach<T>(
  iter: IterableIterator<T>,
  callbackfn: (value: T, index: number) => void,
) {
  let index = 0;
  for (const el of iter) {
    callbackfn(el, index++);
  }
}

export function at<T>(iter: IterableIterator<T>, index: number) {
  checkGreater(index, -1);
  let current = 0;
  for (const el of iter) {
    if (current++ === index) {
      return el;
    }
  }
  return undefined;
}

export function find<T>(iter: IterableIterator<T>, predicate: (el: T) => boolean): T | undefined {
  for (const el of iter) {
    if (predicate(el)) {
      return el;
    }
  }
  return undefined;
}

//
// Functions that return generators.
//

// Generates the integers from `start` (inclusive) to `bound` (exclusive).
export function* rangeIter(bound: number, start = 0): IterableIterator<number> {
  for (let i = start; i < bound; ++i) {
    yield i;
  }
}

// Like Array.map or Array.forEach for iterators, but as a generator so unbounded iterators are
// supported.
export function* mapIter<T, U>(
  iter: IterableIterator<T> | Array<T> | ReadonlyArray<T>,
  callbackfn: (value: T) => U,
): IterableIterator<U> {
  for (const el of iter) {
    yield callbackfn(el);
  }
}

export function filterIter<T, S extends T>(
  iter: IterableIterator<T>,
  predicate: (value: T, index: number) => value is S,
): IterableIterator<S>;

export function filterIter<T>(
  iter: IterableIterator<T>,
  predicate: (value: T, index: number) => boolean,
): IterableIterator<T>;

export function* filterIter<T>(
  iter: IterableIterator<T>,
  predicate: (value: T, index: number) => boolean,
): IterableIterator<unknown> {
  let index = 0;
  for (const el of iter) {
    if (predicate(el, index++)) {
      yield el;
    }
  }
}

export type Generated<T> = T extends AsyncGenerator<infer R> ? R : never;

export async function arrayFromAsync<T>(iterable: AsyncIterable<T>) {
  // Array.fromAsync is very new.
  // return Array.fromAsync(generator);
  const result: T[] = [];
  for await (const el of iterable) {
    result.push(el);
  }
  return result;
}

export async function firstFromAsync<T>(iterable: AsyncIterable<T>) {
  // Array.fromAsync is very new.
  // return Array.fromAsync(generator);
  for await (const el of iterable) {
    return el;
  }
  return undefined;
}

export async function* filterAsyncIterable<T>(
  iterable: AsyncIterable<T>,
  predicate: (t: T) => boolean,
): AsyncIterable<T> {
  for await (const item of iterable) {
    if (predicate(item)) {
      yield item;
    }
  }
}
